<template>
  <div class="bandProfileNavigationWrapper">
    <div class="slidingBackground" :style="sliderStyle" />
    <div class="tw-flex tw-items-center tw-justify-between">
      <div
        v-for="(cell, index) in displayedCells"
        :key="index"
        class="cell tw-flex tw-h-10 tw-cursor-pointer tw-items-center tw-justify-center tw-gap-x-1 tw-text-center tw-text-gray-400 tw-transition-all tw-duration-150 tw-ease-out"
        :style="cellStyle"
        :class="{ selected: index === modelValue }"
        @click="$emit('update:modelValue', index)"
      >
        <div
          class="image tw-relative tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center"
        >
          <i :class="cell.img" />
        </div>
        <div class="text">
          {{ cell.text }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

interface NavigationElement {
  readonly text: string
  readonly img: string
  readonly display: boolean
}

export default defineComponent({
  props: {
    modelValue: {
      type: Number,
      required: true,
    },
  },
  emits: ['update:modelValue'],
  setup() {
    const localePath = useLocalePath()

    return {
      localePath,
    }
  },
  data() {
    return {
      navigation: [
        {
          text: this.$t('band.profile.slug.navigation00'),
          img: 'fa-solid fa-user',
          display: true,
        },
        {
          text: this.$t('band.profile.slug.navigation01'),
          img: 'fa-solid fa-check',
          display: false,
        },
        {
          text: this.$t('band.profile.slug.navigation02'),
          img: 'fa-solid fa-music',
          display: true,
        },
      ] as NavigationElement[],
    }
  },
  computed: {
    displayedCells(): NavigationElement[] {
      return this.navigation.filter((navElement) => navElement.display)
    },
    cellWidthPercent(): number {
      return 100 / this.displayedCells.length
    },
    cellStyle(): Record<string, string> {
      return {
        width: `${this.cellWidthPercent}%`,
      }
    },
    sliderStyle(): Record<string, string> {
      return {
        width: `calc(${this.cellWidthPercent}% - 8px)`,
        left: `calc(${this.cellWidthPercent * this.modelValue}% + 4px)`,
      }
    },
  },
  watch: {
    modelValue() {
      this.$router.replace(
        this.localePath({ query: { tab: this.modelValue.toString() } }),
      )
    },
  },
  mounted() {
    this.handleTabQuery()
  },
  methods: {
    handleTabQuery(): void {
      if (this.$route.query.tab) {
        const tab = Number(this.$route.query.tab)

        if (!isNaN(tab) && tab < this.displayedCells.length && tab > 0)
          this.$emit('update:modelValue', tab)
      }
    },
  },
})
</script>

<style scoped lang="scss">
.bandProfileNavigationWrapper {
  @apply tw-relative tw-w-full tw-border-0 tw-border-b tw-border-t tw-border-solid tw-border-gray-300;
}

.cell.selected {
  @apply tw-text-orange-500;

  .text {
    @apply tw-underline;
  }
}

.slidingBackground {
  @apply tw-absolute tw-top-1 -tw-z-10 tw-h-8 tw-rounded tw-bg-gray-300 tw-transition-all tw-duration-150 tw-ease-out;
}

.image > i {
  text-decoration: unset !important;
}
</style>
